import {motion} from 'framer-motion';
import {useEffect} from 'react';

import {useScrollFreeze} from '../../hooks/useScrollFreeze';
import FluidVideo from '../media/fluidVideo.client';
import {isBrowser} from '@shopify/hydrogen';
import {createPortal} from 'react-dom';

type Props = {
  url: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isGalleryPlayer?: boolean;
};

export default function VideoPopup({url, setIsOpen, isGalleryPlayer}: Props) {
  useScrollFreeze();

  useEffect(() => {
    if (!isBrowser()) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
    };

    window?.addEventListener('keydown', handleEscape);

    return () => {
      window?.removeEventListener('keydown', handleEscape);
    };
  }, [setIsOpen, url, isBrowser()]);

  return createPortal(
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="fixed left-0 top-0 flex h-screen w-screen items-center justify-center"
      style={{zIndex: 90}}
    >
      <motion.div
        onClick={() => setIsOpen(false)}
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        className="fixed left-0 top-0 h-screen w-screen bg-black/50"
      />
      <button
        onClick={() => setIsOpen(false)}
        className="absolute right-gutter top-gutter z-50 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-orange bg-opacity-30 transition-all hover:scale-110 lg:right-10 lg:top-10"
      >
        {isGalleryPlayer ? (
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M59.5 32.5C58.5348 40.945 46.964 56.5 31.5 56.5C17 55.5 5.99992 44 3.99999 31C1.49113 14.6919 14.536 7.5 30 7.5C43.5 10.5 61.5 15 59.5 32.5Z"
              fill="#FEF1E9"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.4574 10.3898C21.4109 4.05698 30.9897 4.17712 41.6514 6.33549C42.4221 6.49152 42.9192 7.23639 42.7615 7.99919C42.6039 8.762 41.8512 9.25389 41.0805 9.09786C40.2149 8.92262 39.3606 8.76197 38.5164 8.61812C43.9701 10.0961 49.7132 12.1116 54.0686 15.235C57.0121 17.3459 59.4003 20.0159 60.6053 23.4369C61.8141 26.869 61.7744 30.894 60.1552 35.6015C56.8927 45.0867 49.6437 52.6643 41.068 56.063C32.4393 59.4827 22.4828 58.6527 14.0789 51.3285C8.44767 46.4206 3.97222 38.7458 2.76798 31.0506C1.55473 23.2979 3.66252 15.3493 11.4574 10.3898ZM31.6578 7.85371C25.1902 7.58608 19.1776 8.82986 12.998 12.7616C6.38513 16.969 4.49527 23.6667 5.58328 30.6191C6.6803 37.6291 10.805 44.7182 15.9617 49.2124C23.4951 55.7781 32.2964 56.5021 40.0089 53.4455C47.7744 50.3679 54.45 43.439 57.4583 34.6928C58.9181 30.449 58.8725 27.0832 57.9152 24.365C56.9539 21.6358 55.0158 19.3962 52.397 17.5182C47.0985 13.7185 39.3396 11.6196 32.5037 10.0277C31.7379 9.84934 31.2632 9.09037 31.4434 8.33247C31.4854 8.15569 31.5594 7.99458 31.6578 7.85371Z"
              fill="#F27021"
            />
            <path
              d="M39.5657 37.6503L39.5491 37.6343C39.3461 37.4631 39.1573 37.2698 38.9742 37.082C38.1458 36.2327 37.2203 35.3983 36.4811 34.4721C36.0323 33.91 35.8729 33.7376 35.78 33.6812C34.9698 32.7202 34.0814 31.7853 33.3018 30.9825C33.6353 30.6688 34.0147 30.327 34.0147 30.327C35.478 29.1596 36.8251 27.9172 38.2518 26.6014C38.6247 26.2575 38.9989 25.9124 39.3679 25.5751C39.8135 25.2192 40.1187 24.8557 40.3006 24.4638C40.4229 24.1992 40.3692 23.9263 40.3176 23.6838C40.3143 23.6068 40.2763 23.5332 40.2395 23.4616C40.2255 23.4346 40.2015 23.3909 40.1995 23.402C40.2308 23.1239 40.0336 22.9738 39.8595 22.8412C39.8194 22.8109 39.7774 22.7789 39.7361 22.7439L39.6887 22.7135L39.6456 22.704C39.6252 22.6996 39.571 22.673 39.5227 22.6439C39.3923 22.484 39.2798 22.3841 39.1563 22.2854L38.9737 22.1684C38.9594 22.158 38.9446 22.1478 38.9063 22.1237L38.8715 22.1007C38.72 22.0024 38.498 21.8997 38.489 21.8952L38.466 21.9389C37.9466 21.7918 37.646 22.036 37.4673 22.3237L37.3628 22.3614L37.3502 22.4727C37.2643 22.539 37.211 22.5765 37.2085 22.5832L37.1558 22.5773L37.1085 22.6017C36.855 22.7322 36.597 22.9968 36.4755 23.1629C36.327 23.2423 36.2306 23.3961 36.1913 23.4684C34.51 24.9181 33.0073 26.5111 31.5538 28.0521L31.0064 28.6315C30.7112 28.3159 30.413 27.9853 30.413 27.9853C29.2463 26.5223 28.0046 25.1756 26.6898 23.7501C26.3452 23.3767 25.9994 23.0018 25.6616 22.632C25.3054 22.186 24.9418 21.8811 24.5503 21.6996C24.2863 21.5774 24.0139 21.6305 23.7707 21.6822C23.6937 21.6852 23.6198 21.7236 23.5485 21.7604C23.5218 21.7744 23.4772 21.7973 23.4892 21.8007C23.2111 21.7695 23.0607 21.9667 22.9283 22.1412C22.8977 22.1812 22.8661 22.2229 22.8311 22.2645L22.8006 22.312L22.7916 22.3547C22.7871 22.3754 22.7602 22.4297 22.7315 22.4777C22.5712 22.608 22.4717 22.7205 22.3727 22.8436L22.2556 23.0266C22.2452 23.0409 22.2354 23.0554 22.2106 23.0943L22.1879 23.1288C22.0897 23.28 21.9866 23.5019 21.9821 23.511L22.0261 23.5339C21.879 24.0534 22.1235 24.3547 22.4112 24.533L22.4497 24.6373L22.5599 24.6497C22.6262 24.7356 22.6637 24.789 22.6708 24.7915L22.6649 24.8442L22.6893 24.8915C22.8198 25.1447 23.084 25.4027 23.2506 25.5242C23.33 25.6727 23.4838 25.7692 23.556 25.8087C25.0043 27.4889 26.5964 28.9905 28.1364 30.4431L28.6123 30.8924C28.4287 31.0449 28.244 31.2047 28.0593 31.3641C27.8076 31.5817 27.556 31.7993 27.3068 31.9981C27.2536 32.0406 27.2092 32.1023 27.1663 32.1629C27.1525 32.1825 27.1324 32.2103 27.1159 32.2311L27.0409 32.2788C26.4412 32.7922 25.908 33.358 25.3924 33.9056C24.8832 34.4458 24.3568 35.005 23.7969 35.4875C23.4578 35.6872 22.6921 36.2971 22.2518 36.7189L22.226 36.7503C22.1576 36.8557 22.1032 36.9308 22.0568 36.9945C21.9769 37.105 21.9134 37.1918 21.8439 37.3423L21.795 37.6986C21.7811 37.8214 21.7885 37.9441 21.8169 38.0657C21.5909 38.3099 21.5653 38.6409 21.736 39.071C21.8044 39.2572 21.9522 39.4423 22.023 39.5316L22.0536 39.5615C22.3545 39.7914 22.6724 39.9244 22.9906 39.9602C23.4683 40.0139 23.9462 39.8486 24.3677 39.4621C24.5386 39.2591 24.7323 39.0703 24.9192 38.8878C25.361 38.4566 25.7805 38.0034 26.2258 37.5755C26.65 37.1681 27.1815 36.8784 27.5296 36.3939C28.0913 35.9455 28.2636 35.7861 28.3204 35.6932C29.254 34.9064 30.1498 34.0574 30.9176 33.3131C31.0878 33.5205 31.2683 33.7292 31.449 33.9382C31.6679 34.1911 31.8864 34.4435 32.0861 34.6943C32.1282 34.7474 32.1904 34.7915 32.2506 34.8343C32.2702 34.8482 32.2983 34.8683 32.3191 34.8848L32.3669 34.9594C32.8803 35.5592 33.4461 36.092 33.9933 36.6076C34.5339 37.1172 35.093 37.6436 35.5759 38.2036C35.7754 38.5433 36.3854 39.3087 36.8073 39.7484L36.8383 39.7741C36.9444 39.8429 37.0191 39.8973 37.0832 39.9437C37.1933 40.0232 37.2805 40.0867 37.4303 40.1562L37.7866 40.2051C37.909 40.2189 38.0322 40.2112 38.1541 40.1829C38.3979 40.4089 38.7292 40.4348 39.1593 40.2642C39.3466 40.1951 39.531 40.0477 39.6199 39.9768L39.6499 39.9459C40.2236 39.1956 40.1927 38.3367 39.5657 37.6503Z"
              fill="#F27021"
            />
          </svg>
        ) : (
          <svg
            className="rotate-45"
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="transition-standard"
              d="M13 0C12.2238 0 11.5947 0.629119 11.5947 1.40531V11.5947H1.40531C0.629119 11.5947 0 12.2238 0 13C0 13.7762 0.629119 14.4053 1.40531 14.4053H11.5947V24.5947C11.5947 25.3709 12.2238 26 13 26C13.7762 26 14.4053 25.3709 14.4053 24.5947V14.4053H24.5947C25.3709 14.4053 26 13.7762 26 13C26 12.2238 25.3709 11.5947 24.5947 11.5947H14.4053V1.40531C14.4053 0.629119 13.7762 0 13 0Z"
              fill="#F27021"
            />
          </svg>
        )}
      </button>

      <div className="relative w-full max-w-6xl sm:mx-gutter 2xl:max-w-[1440px]">
        <div className="relative  z-10 overflow-hidden lg:rounded-xl">
          <FluidVideo url={url} playing controls />
        </div>
      </div>
    </motion.div>,
    document.body,
  );
}
